import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';

import { unset } from 'lodash';
import { catchError, map } from 'rxjs/operators';
import { Observable, of, BehaviorSubject } from 'rxjs';

import { User } from '../_models/user.model';
import { Permission } from '../_models/permission.model';
import { Role } from '../_models/role.model';
import { QueryParamsModel } from '../../_base/crud';
import { StorageService } from '../../_services/storage.service';
import { httpParams } from '../../_utils';
import { environment } from '../../../../environments/environment';

const API_USERS_URL = `${environment.apiPrefix}/users`;
const API_AUTH_URL = `${environment.apiPrefix}/auth`;
const API_PERMISSION_URL = `${environment.apiPrefix}/permissions`;
const API_ROLES_URL = `${environment.apiPrefix}/roles`;

@Injectable()
export class AuthService {
  public hasQlik$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor(private http: HttpClient, private storage: StorageService) {}
  // Authentication/Authorization
  login(authData: any): Observable<{ code: number; user: User; access_token?: string }> {
    let headers = new HttpHeaders();

    if (authData.tfa) {
      headers = headers.set('tfa-key', authData.tfa);
      unset(authData, 'tfa');
    }

    return this.http.post<{ code: number; user: User; access_token?: string }>(`${API_AUTH_URL}/login`, authData, {
      headers,
    });
  }

  getUserByToken(): Observable<User> {
    return this.http.get<User>(`${API_AUTH_URL}/me`);
  }

  // CREATE =>  POST: add a new user to the server
  register(user: User): Observable<any> {
    return this.http.post<User>(API_USERS_URL, user).pipe(
      map((res: User) => {
        return res;
      }),
      catchError((err) => {
        return null;
      }),
    );
  }

  /*
   * Submit forgot password request
   *
   * @param {string} email
   * @returns {Observable<any>}
   */
  public requestPassword(data: any): Observable<any> {
    return this.http
      .post(`${API_USERS_URL}/reset-password`, data)
      .pipe(catchError(this.handleError('forgot-password', [])));
  }

  // Method from server should return QueryResultsModel(items: any[], totalsCount: number)
  // items => filtered/sorted result
  getAllUsers(queryParams: QueryParamsModel = null): Observable<{ users: User[]; metadata: any }> {
    return this.http.get<{ users: User[]; metadata: any }>(API_USERS_URL, { params: httpParams(queryParams) });
  }

  getUserById(userId: number): Observable<User> {
    return this.http.get<User>(API_USERS_URL + `/${userId}`);
  }

  // DELETE => delete the user from the server
  deleteUser(userId: number) {
    const url = `${API_USERS_URL}/${userId}`;
    return this.http.delete(url);
  }

  // UPDATE => PUT: update the user on the server
  updateUser(user: User): Observable<any> {
    return this.http.put(`${API_USERS_URL}/${user.id}`, user);
  }

  // Permission
  getAllPermissions(queryParams: any = null): Observable<{ permissions: Permission[]; metadata: any }> {
    return this.http.get<{ permissions: Permission[]; metadata: any }>(API_PERMISSION_URL, {
      params: httpParams(queryParams),
    });
  }

  getRolePermissions(roleId: number): Observable<Permission[]> {
    return this.http.get<Permission[]>(API_PERMISSION_URL, { params: httpParams({ roleId }) });
  }

  // Roles
  getAllRoles(queryParams: QueryParamsModel = null): Observable<{ roles: Role[]; metadata: any }> {
    return this.http.get<{ roles: Role[]; metadata: any }>(API_ROLES_URL, { params: httpParams(queryParams) });
  }

  getRoleById(roleId: number): Observable<Role> {
    return this.http.get<Role>(API_ROLES_URL + `/${roleId}`);
  }

  // CREATE =>  POST: add a new role to the server
  createRole(role: Role): Observable<Role> {
    return this.http.post<Role>(API_ROLES_URL, role);
  }

  // UPDATE => PUT: update the role on the server
  updateRole(role: Role): Observable<any> {
    return this.http.put(`${API_ROLES_URL}/${role.id}`, role);
  }

  // DELETE => delete the role from the server
  deleteRole(roleId: number): Observable<Role> {
    const url = `${API_ROLES_URL}/${roleId}`;
    return this.http.delete<Role>(url);
  }

  /*
   * Handle Http operation that failed.
   * Let the app continue.
   *
   * @param operation - name of the operation that failed
   * @param result - optional value to return as the observable result
   */
  private handleError<T>(operation = 'operation', result?: any) {
    return (error: any): Observable<any> => {
      // TODO: send the error to remote logging infrastructure
      console.error(error); // log to console instead

      // Let the app keep running by returning an empty result.
      return of(result);
    };
  }
}
